import type { ShipmentDetailsData } from "~/shared/schemas/shipment-management-schema.ts";

type Stop = ShipmentDetailsData["pickup"];

export type StopValidationErrors = {
	companyName?: string;
	locationType?: string;
	phone?: string;
	shipmentHoursFrom?: string;
	shipmentHoursTo?: string;
	scheduled?: string;
};

export type AdditionalDetailsValidationErrors = {
	emergencyContactName?: string;
	emergencyContactNumber?: string;
};

export type ShipmentValidationErrors = {
	pickup: StopValidationErrors;
	delivery: StopValidationErrors;
	additionalDetails: AdditionalDetailsValidationErrors;
};

const REQUIRED_MESSAGE = "This field is required.";

const isBlank = (value?: string | null): boolean =>
	!value || value.trim().length === 0;

// Company name, location type, hours from/to and scheduled are required on a
// pickup/delivery stop. Contact name is optional on both stops; phone is
// required on delivery only (controlled by `requirePhone`).
const validateStop = (
	stop: Stop,
	requirePhone: boolean,
): StopValidationErrors => {
	const errors: StopValidationErrors = {};
	if (isBlank(stop.companyName)) {
		errors.companyName = REQUIRED_MESSAGE;
	}
	if (isBlank(stop.locationType)) {
		errors.locationType = REQUIRED_MESSAGE;
	}
	if (requirePhone && isBlank(stop.contact?.phone)) {
		errors.phone = REQUIRED_MESSAGE;
	}
	if (isBlank(stop.shipmentHoursFrom)) {
		errors.shipmentHoursFrom = REQUIRED_MESSAGE;
	}
	if (isBlank(stop.shipmentHoursTo)) {
		errors.shipmentHoursTo = REQUIRED_MESSAGE;
	}
	if (isBlank(stop.scheduled)) {
		errors.scheduled = REQUIRED_MESSAGE;
	}
	return errors;
};

// Emergency contact name/number are only required once at least one commodity
// on the shipment is marked hazmat.
const validateAdditionalDetails = (
	draft: ShipmentDetailsData,
): AdditionalDetailsValidationErrors => {
	const hasHazmat = (draft.freightDetails?.lineItems ?? []).some(
		(lineItem) => lineItem.hazmat,
	);
	if (!hasHazmat) {
		return {};
	}

	const errors: AdditionalDetailsValidationErrors = {};
	if (isBlank(draft.additionalDetails?.emergencyContactName)) {
		errors.emergencyContactName = REQUIRED_MESSAGE;
	}
	if (isBlank(draft.additionalDetails?.emergencyContactNumber)) {
		errors.emergencyContactNumber = REQUIRED_MESSAGE;
	}
	return errors;
};

/**
 * Validates the required fields on the shipment edit draft: pickup and delivery
 * company name, location type, hours from/to and scheduled; delivery phone.
 * Contact name is optional on both stops and pickup phone is optional.
 */
export const validateShipmentDraft = (
	draft: ShipmentDetailsData,
): ShipmentValidationErrors => {
	const errors: ShipmentValidationErrors = {
		pickup: validateStop(draft.pickup, false),
		delivery: validateStop(draft.delivery, true),
		additionalDetails: validateAdditionalDetails(draft),
	};
	return errors;
};

// Whether any required field failed validation.
export const hasShipmentValidationErrors = (
	errors: ShipmentValidationErrors,
): boolean =>
	Object.keys(errors.pickup).length > 0 ||
	Object.keys(errors.delivery).length > 0 ||
	Object.keys(errors.additionalDetails).length > 0;
